import { DateTime } from 'luxon-business-days';
import { getDiffInCalendarDays } from 'utils/date';
import { toFormattedDateFromLuxonDate, DATE_FORMAT } from 'utils/date';
import { TRANSACTION_CREATED_FIELD } from 'resources/deal/deal.constants';
import { COMMON_COMPUTED_FIELDS } from 'components/transaction/advancedFields/index.helpers';
import { getTransactionStartDate, getTransactionClosingOn, getTransactionUnderContractDate } from 'resources/deal/deal.helpers';

// We do not count 0 as empty, because it's a valid value for dueInDays
const isEmptyDueInDays = (dueInDays) => dueInDays === null || dueInDays === undefined;


// ----- Common for Milestones/Tasks/Emails/Audits ----->

/**
 * @returns {string | null} Relative To Date as ISO string (date part only)
 */
const getRelativeToDate = (dueRelativeDate, deal) => {
  switch (dueRelativeDate) {
    case TRANSACTION_CREATED_FIELD.NAME:
      return getTransactionStartDate(deal);
    case COMMON_COMPUTED_FIELDS.CLOSING_DATE.NAME:
      return getTransactionClosingOn(deal);
    case COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME:
      return getTransactionUnderContractDate(deal);
    default:
      return deal.advanced_fields[dueRelativeDate];
  }
};

/**
 * @returns {DateTime | null} Transaction Milestone/Task Due Date as luxon DateTime object
 */
const getDueDate = ({ dueInDays, isAfterAction, dueRelativeDate, deal, calculationSettings = null }) => {
  const relativeToDateIsoString = getRelativeToDate(dueRelativeDate, deal);
  if (relativeToDateIsoString) {
    const luxonRelativeToDate = DateTime.fromISO(relativeToDateIsoString);

    if (calculationSettings?.exclude_weekends) {
      // Disable holidays for business days calculation
      luxonRelativeToDate.setupBusiness({ holidayMatchers: [] });

      return isAfterAction
        ? luxonRelativeToDate.plusBusiness({ days: dueInDays })
        : luxonRelativeToDate.minusBusiness({ days: dueInDays });
    }

    return isAfterAction
      ? luxonRelativeToDate.plus({ days: dueInDays })
      : luxonRelativeToDate.minus({ days: dueInDays });
  }

  return null;
};

/**
 * @returns {number | null} Transaction Milestone/Task Due Date as number of days relative to the relativeToDate. Can be negative.
 */
const getDueInDays = ({ dueDateIsoString, dueRelativeDate, isAfterAction, deal }) => {
  if (!dueDateIsoString) return null;

  const dueDate = DateTime.fromISO(dueDateIsoString, { zone: 'utc' });
  const relativeToDate = DateTime.fromISO(getRelativeToDate(dueRelativeDate, deal), { zone: 'utc' });

  const fromDate = isAfterAction ? relativeToDate : dueDate;
  const toDate = isAfterAction ? dueDate : relativeToDate;

  const diffInDays = getDiffInCalendarDays(fromDate, toDate);
  return diffInDays;
};

/**
 * @param {DateTime | null} dueDate - Due Date as luxon DateTime object
 * @returns {string} Transaction Milestone/Task Due Date text color
 */
export const getDueDateTextColor = ({ dueDate }) => {
  if (dueDate) {
    const diffInDays = getDiffInCalendarDays(DateTime.now(), dueDate);
    const isSoon = diffInDays > 0 && diffInDays <= 3;
    const isOverdue = diffInDays <= 0;
  
    if (isSoon || isOverdue) 
      return 'var(--red-600)';
  }
    
  return 'var(--gray-500)';
};

export const isDueRelativeDateValueEmpty = (dueRelativeDate, getValue) => {
  if (!dueRelativeDate)
    return false;

  // transaction created_on date is always set, so we don't need to check if it's empty
  if (dueRelativeDate === TRANSACTION_CREATED_FIELD.NAME)
    return false;
  
  return !getValue(dueRelativeDate);
};

// ----- Common for Milestones/Tasks/Emails/Audits <-----


// ----- Audits ----->

/**
 * @returns {DateTime | null} Transaction Audit Due Date as luxon DateTime object
 */
export const getDealAuditDueDate = ({ audit, deal, calculationSettings = null }) => {
  const dueDate = getDueDate({
    dueInDays: audit.due_in_days,
    isAfterAction: audit.is_after_action,
    dueRelativeDate: audit.due_relative_date,
    deal,
    calculationSettings,
  });

  return dueDate;
};

/**
 * @returns {number | null} Transaction Audit Due Date as number of days relative to the relativeToDate. Can be negative.
 */
export const getDealAuditDueInDays = ({ dueDateIsoString, audit, deal }) => {
  const dueInDays = getDueInDays({
    dueDateIsoString,
    dueRelativeDate: audit.due_relative_date,
    isAfterAction: audit.is_after_action,
    deal,
  });

  return dueInDays;
};

/**
 * @returns { string } Transaction Audit Due Date as formatted string
 * @example 'Due Dec 15, 2024'
 */
export const getDealAuditDueDateText = ({ audit, deal }) => {
  const dueDate = getDealAuditDueDate({ audit, deal });
  if (!dueDate)
    return 'Missing Info!';

  return toFormattedDateFromLuxonDate(dueDate);
};

export const getDealAuditDueDateTextColor = ({ audit, deal }) => {
  const dueDate = getDealAuditDueDate({ audit, deal });
  return getDueDateTextColor({ dueDate });
};

/**
 * @returns { string } Kit Audit Due Date as formatted string
 * @example 'Due 7 days before Closing Date'
 */
export const getKitAuditDueDateText = ({ audit, companyAdvancedFields }) => {
  const { due_in_days, is_after_action, due_relative_date } = audit;

  const relativeToDateLabel = due_relative_date === TRANSACTION_CREATED_FIELD.NAME
    ? TRANSACTION_CREATED_FIELD.LABEL
    : companyAdvancedFields?.find(field => field.name === due_relative_date)?.label;

  return `${due_in_days} day${due_in_days === 1 ? '' : 's'} ${is_after_action ? 'after' : 'before'} ${relativeToDateLabel || due_relative_date}`
};

// ----- Audits <-----


// ----- Milestones ----->

/**
 * @returns {DateTime | null} Transaction Milestone Due Date as luxon DateTime object
 */
export const getDealMilestoneDueDate = ({ milestone, deal, calculationSettings = null }) => {
  const dueDate = getDueDate({
    dueInDays: milestone.due_in_days,
    isAfterAction: milestone.is_after_action,
    dueRelativeDate: milestone.due_relative_date,
    deal,
    calculationSettings,
  });

  return dueDate;
};

/**
 * @returns {number | null} Transaction Milestone Due Date as number of days relative to the relativeToDate. Can be negative.
 */
export const getDealMilestoneDueInDays = ({ dueDateIsoString, milestone, deal }) => {
  const dueInDays = getDueInDays({
    dueDateIsoString,
    dueRelativeDate: milestone.due_relative_date,
    isAfterAction: milestone.is_after_action,
    deal,
  });

  return dueInDays;
};

/**
 * @returns { string } Transaction Milestone Due Date as formatted string
 * @example
 * 'No due date'
 * 'Dec 15, 2024'
 */
export const getDealMilestoneDueDateText = ({ milestone, deal }) => {
  const { due_in_days } = milestone;

  if (isEmptyDueInDays(due_in_days)) {
    return 'No due date';
  }

  const dueDate = getDealMilestoneDueDate({ milestone, deal });
  if (!dueDate)
    return 'Missing Info!';

  return toFormattedDateFromLuxonDate(dueDate);
};

export const getDealMilestoneDueDateTextColor = ({ milestone, deal }) => {
  const dueDate = getDealMilestoneDueDate({ milestone, deal });
  return getDueDateTextColor({ dueDate });
};

/**
 * @returns { string } Kit Milestone Due Date as formatted string
 * @example
 * 'No due date'
 * '5 days after Transaction Created Date'
 * '7 days before Closing Date'
 */
export const getKitMilestoneDueDateText = ({ milestone, companyAdvancedFields }) => {
  const { due_in_days, is_after_action, due_relative_date } = milestone;

  if (isEmptyDueInDays(due_in_days)) {
    return 'No due date';
  }

  const relativeToDateLabel = due_relative_date === TRANSACTION_CREATED_FIELD.NAME
    ? TRANSACTION_CREATED_FIELD.LABEL
    : companyAdvancedFields?.find(field => field.name === due_relative_date)?.label;

  return `${due_in_days} day${due_in_days === 1 ? '' : 's'} ${is_after_action ? 'after' : 'before'} ${relativeToDateLabel || due_relative_date}`
};

// ----- Milestones <-----


// ----- Tasks/Emails ----->

/**
 * @returns {DateTime | null} Transaction Task Due Date as luxon DateTime object
 */
export const getDealTaskDueDate = ({ task, deal, calculationSettings = null }) => {
  const dueDate = getDueDate({
    dueInDays: task.due_in_days,
    isAfterAction: task.is_after_action,
    dueRelativeDate: task.due_relative_date,
    deal,
    calculationSettings,
  });

  return dueDate;
};

/**
 * @returns {number | null} Transaction Task Due Date as number of days relative to the relativeToDate. Can be negative.
 */
export const getDealTaskDueInDays = ({ dueDateIsoString, task, deal }) => {
  const dueInDays = getDueInDays({
    dueDateIsoString,
    dueRelativeDate: task.due_relative_date,
    isAfterAction: task.is_after_action,
    deal,
  });

  return dueInDays;
};

/**
 * @returns { string } Transaction Task Due Date as formatted string
 * @example 'Dec 15, 2024'
 */
export const getDealTaskDueDateText = ({ task, deal }) => {
  const { due_in_days } = task;

  if (isEmptyDueInDays(due_in_days)) {
    return null;
  }

  const dueDate = getDealTaskDueDate({ task, deal });
  if (!dueDate)
    return 'Missing Info!';

  return toFormattedDateFromLuxonDate(dueDate);
};

export const getDealTaskDueDateTextColor = ({ task, deal }) => {
  const dueDate = getDealTaskDueDate({ task, deal });
  return getDueDateTextColor({ dueDate });
};

/**
 * @returns { string } Kit Task Due Date as formatted string
 * @example
 * '5 days after Transaction Created Date'
 * '7 days before Closing Date'
 */
export const getKitTaskDueDateText = ({ task, companyAdvancedFields }) => {
  const { due_in_days, is_after_action, due_relative_date } = task;

  if (isEmptyDueInDays(due_in_days)) {
    return null;
  }

  const relativeToDateLabel = due_relative_date === TRANSACTION_CREATED_FIELD.NAME
    ? TRANSACTION_CREATED_FIELD.LABEL
    : companyAdvancedFields?.find(field => field.name === due_relative_date)?.label;

  return `${due_in_days} day${due_in_days === 1 ? '' : 's'} ${is_after_action ? 'after' : 'before'} ${relativeToDateLabel || due_relative_date}`
};

// ----- Tasks/Emails <-----



